import {
	styled,
	ThemeProvider as MUIThemeProvider,
} from '@mui/material/styles';
import classNames from 'classnames';
import { useEffect } from 'react';
import { theme } from './theme';
import { RootGlobalStyles } from './RootGlobalStyles';

export const ThemeProvider = ({
	children,
	themeName,
}: React.PropsWithChildren<{ themeName?: string }>) => {
	useEffect(() => {
		// const template = document.createElement('template');
		const robotoFont = document.createElement('link');
		robotoFont.href =
			'https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap';
		robotoFont.rel = 'stylesheet';
		robotoFont.id = 'roboto-font';
		const existingRobotoFont = document.getElementById('roboto-font');
		if (!existingRobotoFont) {
			document.head.appendChild(robotoFont);
		}

		const ubuntuFont = document.createElement('link');
		ubuntuFont.href =
			'https://fonts.googleapis.com/css2?family=Ubuntu:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap';
		ubuntuFont.rel = 'stylesheet';
		ubuntuFont.id = 'ubuntu-font';
		const existingUbuntuFont = document.getElementById('ubuntu-font');
		if (!existingUbuntuFont) {
			document.head.appendChild(ubuntuFont);
		}
	}, []);

	return (
		<MUIThemeProvider theme={theme}>
			<RootGlobalStyles />
			<ThemeWrapper themeName={themeName}>{children}</ThemeWrapper>
		</MUIThemeProvider>
	);
};

const ThemeWrapper = styled(
	({
		className,
		themeName,
		children,
	}: React.PropsWithChildren<{ className?: string; themeName?: string }>) => {
		return (
			<div
				className={classNames(
					className,
					'theme',
					themeName && `theme--${themeName}`,
				)}
			>
				{children}
			</div>
		);
	},
	{ label: 'ThemeWrapper', target: 'ThemeWrapper-root' },
)(({ theme }) => ({
	display: 'contents',

	'--color--white': theme.colors.white,
	'--color--lightest-blue': theme.colors.lightestBlue,
	'--color--lighter-blue': theme.colors.lighterBlue,
	'--color--light-blue': theme.colors.lightBlue,
	'--color--blue': theme.colors.blue,
	'--color--dark-blue': theme.colors.darkBlue,
	'--color--gray': theme.colors.gray,
	'--color--light-gray': theme.colors.lightGray,
	'--color--lighter-gray': theme.colors.lighterGray,
	'--color--dark-gray': theme.colors.darkGray,
	'--color--darker-gray': theme.colors.darkerGray,
	'--color--mustard': theme.colors.mustard,
	'--color--light': theme.colors.light,
	'--color--bordeaux': theme.colors.bordeaux,
	'--color--dark': theme.colors.dark,
	'--color--black': theme.colors.black,
	'--color--red': theme.colors.red,
	'--color--green': theme.colors.green,
	'--color--yellow': theme.colors.yellow,
	'--color--teal': theme.colors.teal,
	'--color--transparent': 'transparent',

	// base
	'--theme--primary': 'var(--color--dark)',
	'--theme--secondary': 'var(--color--black)',
	'--theme--info': 'var(--color--teal)',
	'--theme--warning': 'var(--color--yellow)',
	'--theme--danger': 'var(--color--red)',
	'--theme--light-primary': 'var(--color--gray)',
	'--theme--light-secondary': 'var(--color--white)',
	'--theme--success': 'var(--color--green)',

	'--theme--text': 'var(--theme--primary)',
	'--theme--light-text': 'var(--color--dark-gray)',
	'--theme--background': 'var(--color--lighter-gray)',
	'--theme--background-hover': 'var(--color--light-gray)',

	'--overlay_background': theme.mixins.hexWithOpacity(
		theme.colors.darkerGray,
		0.34,
	),

	// FORM ELEMENT
	'--form__element_color': 'var(--theme--text)',
	'--form__element--hover_color': 'var(--form__element_color)',
	'--form__element__label_color': 'var(--form__element_color)',
	'--form__element__placeholder_color': 'var(--theme--light-primary)',
	'--form__element_background': 'var(--color--lighter-gray)',
	'--form__element--hover_background': 'var(--theme--background-hover)',
	'--form__element--active_color': 'var(--theme--light-secondary)',
	'--form__element--active_background': 'var(--theme--secondary)',
	'--form__element_border-color': 'var(--form__element__placeholder_color)',
	'--form__element--error_color': 'var(--theme--danger)',
	'--form__element--error__placeholder_color':
		'var(--form__element--error_color)',

	'--form__help_color': 'var(--theme--light-text)',
	'--form__required_color': 'var(--theme--secondary)',
	'--form__error_color': 'var(--form__element--error_color)',

	'--form__submit_border': 'var(--form__element_color)',

	'--form__input__icon_color': 'var(--form__element_color)',

	'--form__checkbox__label_color': 'var(--form__element_color)',
	'--form__checkbox--checked__label_color':
		'var(--form__checkbox__label_color)',
	'--form__checkbox--error_color': 'var(--form__element--error_color)',
	'--form__checkbox--disabled_color': 'var(--form__element__placeholder_color)',
	'--form__checkbox__icon_fill': 'var(--color--transparent)',
	'--form__checkbox__icon_stroke': 'var(--form__element_color)',
	'--form__checkbox--checked__icon_fill':
		'var(--form__element--active_background)',
	'--form__checkbox--checked__icon_stroke':
		'var(--form__checkbox__icon_stroke)',

	'--form__radio-list_color': 'var(--form__element_color)',

	'--form__radio-list__item__label_color': 'var(--form__radio-list_color)',
	'--form__radio-list__item--checked__label_color':
		'var(--form__radio-list__item__label_color)',
	'--form__radio-list__item__icon_border': 'var(--theme--text)',
	'--form__radio-list__item__icon_background': 'var(--color--transparent)',
	'--form__radio-list__item--checked__icon_border':
		'var(--form__radio-list__item__icon_border)',
	'--form__radio-list__item--checked__icon_background':
		'var(--form__element--active_background)',
	'--form__radio-list__item--disabled_color':
		'var(--form__element__placeholder_color)',

	'--form__chooser__item_background': 'var(--form__element_background)',
	'--form__chooser__item--hover_background':
		'var(--form__element--hover_background)',
	'--form__chooser__item_color': 'var(--form__element_color)',
	'--form__chooser__item--hover_color': 'var(--form__element--hover_color)',
	'--form__chooser__item--checked__label_background':
		'var(--form__element--active_background)',
	'--form__chooser__item--checked__label_color':
		'var(--form__element--active_color)',

	'--form__select__option_color': 'var(--theme--text)',
	'--form__select__option--hover_color': 'var(--form__select__option_color)',
	'--form__select__option_background': 'var(--form__element_background)',
	'--form__select__option--hover_background':
		'var(--form__element--hover_background)',
	'--form__select__option--selected_color': 'var(--form__element_color)',
	'--form__select__option--selected_background':
		'var(--form__element--active_background)',
	'--form__select__option--last_border': 'var(--form__element_background)',
	'--form__select--error__placeholder_color':
		'var(--form__element--error_color)',
	'--form__select__placeholder_color': 'var(--theme--light-text)',
	'--form__select__control_background': 'var(--form__element_background)',
	'--form__select__indicator_color': 'var(--theme--light-text)',
	'--form__select__indicator--hover_color': 'var(--form__element--hover_color)',

	'--form__date_background': 'var(--form__element_background)',
	'--form__date_color': 'var(--color--dark-gray)',
	'--form__date_placeholder': 'var(--form__date_color)',
	// --form__date__arrow_color: var(--form__date_color);
	// --form__date--hover__arrow_color: var(--form__date__arrow_color);

	'--form__rating__item_color': 'var(--form__element--active_color)',
	'--form__rating__item--checked_color':
		'var(--form__element--active_background)',

	'--form__textarea_color': 'var(--form__element_color)',
	'--form__textarea_background': 'var(--theme--background)',
	'--form__textarea__placeholder_color': 'var(--theme--light-text)',

	// BUTTON
	'--button_background': 'var(--theme--primary)',
	'--button_color': 'var(--theme--light-secondary)',
	'--button--hover_background': 'var(--theme--secondary)',
	'--button--hover_color': 'var(--button_color)',

	'--button--secondary_background': 'var(--theme--secondary)',
	'--button--secondary_color': 'var(--theme--light-secondary)',
	'--button--secondary--hover_background':
		'var(--button--secondary_background)',
	'--button--secondary--hover_color': 'var(--button--secondary_color)',

	'--button--success_background': 'var(--theme--success)',
	'--button--success_color': 'var(--color--white)',
	'--button--success--hover_background': 'var(--button--success_background)',
	'--button--success--hover_color': 'var(--button--success_color)',

	'--button--warning_background': 'var(--theme--warning)',
	'--button--warning_color': 'var(--color--dark)',
	'--button--warning--hover_background': 'var(--theme--warning)',
	'--button--warning--hover_color': 'var(--button--warning_color)',

	'--button--danger_background': 'var(--theme--danger)',
	'--button--danger_color': 'var(--color--white)',
	'--button--danger--hover_background': 'var(--button--danger_background)',
	'--button--danger--hover_color': 'var(--button--danger_color)',

	'--button--info_background': 'var(--theme--info)',
	'--button--info_color': 'var(--color--white)',
	'--button--info--hover_background': 'var(--button--info_background)',
	'--button--info--hover_color': 'var(--button--info_color)',

	'--button--hover_opacity': 0.8,
	'--button--disabled_opacity': 0.4,

	// BREAKLINE
	'--breakline_color': 'var(--theme--secondary)',

	// POPUP
	'--popup__close_color': 'var(--theme--primary)',
	'--popup__close_background': 'var(--color--lighter-blue)',

	// TOGGLER
	'--toggler--primary': 'var(--theme--primary)',
	'--toggler--secondary': 'var(--theme--secondary)',
	'--toggler--light': 'var(--theme--light-primary)',

	'--toggler_color': 'var(--toggler--light)',
	'--toggler_border': 'var(--toggler--primary)',
	'--toggler__dot_background': 'var(--toggler_color)',
	'--toggler--off_color': 'var(--color--darker-gray)',
	// FRAME
	'--chat__frame_border': 'var(--theme--primary)',
	'--chat__frame_background': 'var(--color--white)',
	'--chat__reconnect_color': 'var(--theme--danger)',
	'--chat__shadow_background': theme.mixins.hexWithOpacity(
		theme.colors.black,
		0.15,
	),
	// SCROLLBAR
	'--chat__scrollbar_color': 'var(--theme--light-primary)',
	// FOOTER
	'--chat__footer_background': 'var(--theme--background)',
	'--chat__footer_color': 'var(--theme--text)',
	'--chat__footer__form__placeholder_color': 'var(--theme--light-text)',
	'--chat__footer__form__attach_color': 'var(--chat__footer_background)',
	'--chat__footer__form__submit_color': 'var(--theme--secondary)',
	'--chat__footer__form__action_color': 'var(--theme--text)',
	'--chat__footer__form__action--hover_color': 'var(--theme--secondary)',
	'--chat__footer__form__action--disabled_color': 'var(--theme--light-primary)',
	'--chat__footer__form__attach--hover_color':
		'var(--chat__footer__form__action--hover_color)',
	'--chat__footer__form__record-audio--hover_color':
		'var(--chat__footer__form__action--hover_color)',
	'--chat__footer__form__preview_border':
		'var(--chat__footer__form__action_color)',
	'--chat__footer__form__recording-audio_color': 'var(--theme--danger)',
	// POWERED-BY
	'--chat__footer__powered-by_color': 'var(--theme--light-text)',
	'--chat__footer__powered-by_background': 'var(--chat__frame_background)',
	'--chat__footer__powered-by_border': 'var(--chat__frame_border)',
	// HEADER
	'--chat__header_background': 'var(--theme--primary)',
	'--chat__header_color': 'var(--color--white)',
	'--chat__header__title_color': 'var(--chat__header_color)',
	'--chat__header__description_color': 'var(--chat__header_color)',
	'--chat__header__settings_color': 'var(--chat__header_color)',
	'--chat__header__close_color': 'var(--chat__header_color)',
	'--chat__header__minimize_color': 'var(--chat__header_color)',
	'--chat__header__logo_color': 'var(--chat__header_color)',
	'--chat__header__logo_opacity': 1,
	// WAVE
	'--chat__wave_color': 'var(--chat__header_background)',
	'--chat__wave_border': 'var(--chat__frame_border)',
	'--chat__wave_border_width': '0px',

	// MODAL
	'--chat__modal_background': 'var(--color--white)',
	'--chat__modal_color': 'var(--theme--text)',
	'--chat__modal__header_color': 'var(--chat__modal_color)',
	'--chat__modal__footer_color': 'var(--chat__modal_color)',
	'--chat__modal_border': 'var(--chat__modal_color)',
	'--chat__modal__list__item_color': 'var(--chat__modal_color)',
	'--chat__modal__list__item--hover_background':
		'var(--theme--background-hover)',
	'--chat__modal__list__item__icon_color':
		'var(--chat__modal__list__item_color)',
	'--chat__modal__close_color': 'var(--theme--secondary)',
	'--chat__modal__overlay_background': 'var(--overlay_background)',

	// ICONS
	'--icon_color': 'var(--theme--primary)',

	// MESSAGE
	'--message_color': 'var(--theme--text)',
	'--message__balloon_color': 'var(--message_color)',
	'--message__balloon_background': 'var(--theme--background)',
	'--message--left__balloon_background': 'var(--message__balloon_background)',
	'--message--right__balloon_background': 'var(--message__balloon_background)',
	'--message--selected__balloon_border': 'var(--theme--secondary)',
	// --message--selected__selected-icon_color: var(
	// 	--message--selected__balloon_border
	// );
	'--message__footer_color': 'var(--theme--text)',
	'--message__sender_color': 'var(--message_color)',
	'--message__avatar__icon_color': 'var(--theme--light-primary)',
	'--message__avatar--system__icon_color': 'var(--message__avatar__icon_color)',
	'--message__avatar--agent__icon_color': 'var(--message__avatar__icon_color)',
	'--message__avatar--user__icon_color': 'var(--message__avatar__icon_color)',
	'--message__avatar__shape_color': 'var(--theme--background)',
	'--message__avatar--system__shape_color':
		'var(--message__avatar__shape_color)',
	'--message__avatar--agent__shape_color':
		'var(--message__avatar__shape_color)',
	'--message__avatar--user__shape_color': 'var(--message__avatar__shape_color)',
	'--message__details__icon_color': 'var(--chat__footer__form__action_color)',
	'--message__details_color': 'var(--theme--text)',
	'--message__quick-replies__option_background': 'var(--button_background)',
	'--message__quick-replies__option_color': 'var(--button_color)',
	'--message__quick-replies__option--hover_background':
		'var(--button--hover_background)',
	'--message__quick-replies__option--hover_color':
		'var(--message__quick-replies__option_color)',
	'--message__retry-send_color': 'var(--chat__reconnect_color)',

	'--message__form__element_background': 'var(--chat__frame_background)',
	'--message__form__element_color': 'var(--message__balloon_color)',
	'--message__form__element--hover_color':
		'var(--message__form__element_color)',
	'--message__form__element__label_color':
		'var(--message__form__element_color)',
	'--message__form__element__placeholder_color': 'var(--theme--light-text)',
	'--message__form__element--hover_background':
		'var(--theme--background-hover)',
	'--message__form__element--active_color': 'var(--theme--light-secondary)',
	'--message__form__element--active_background': 'var(--theme--secondary)',
	'--message__form__element_border-color':
		'var(--message__form__element__placeholder_color)',
	'--message__form__element--error_color': 'var(--theme--danger)',
	'--message__form__element--error__placeholder_color':
		'var(--message__form__element--error_color)',

	'--message__form__help_color':
		'var(--message__form__element__placeholder_color)',
	'--message__form__required_color': 'var(--form__required_color)',
	'--message__form__error_color': 'var(--message__form__element--error_color)',

	'--message__form__chooser__item_color': 'var(--form__chooser__item_color)',
	'--message__form__chooser__item--hover_color':
		'var(--message__form__chooser__item_color)',
	'--message__form__chooser__item_background':
		'var(--message__form__element_background)',
	'--message__form__chooser__item--hover_background':
		'var(--theme--background-hover)',
	'--message__form__rating__item_color': 'var(--form__rating__item_color)',
	'--message__form__rating__item--checked_color':
		'var(--form__rating__item--checked_color)',
	'--message__form__date_background':
		'var(--message__form__element_background)',
	'--message__form__radio-list__label_color':
		'var(--message__form__element_color)',
	'--message__form__radio-list__item__icon_border':
		'var(--message__form__element_color)',
	'--message__form__radio-list__item__icon_background':
		'var(--message__balloon_background)',
	'--message__form__radio-list__item--checked__icon_border':
		'var(--message__form__radio-list__item__icon_border)',
	'--message__form__radio-list__item--checked__icon_background':
		'var(--form__radio-list__item--checked__icon_background)',
	'--message__form__select__control_background':
		'var(--message__form__element_background)',
	'--message__form__select__option_background':
		'var(--message__form__element_background)',
	'--message__form__select__option_color':
		'var(--message__form__element_color)',
	'--message__form__select__option--hover_background':
		'var(--message__form__element--hover_background)',
	'--message__form__select__option--hover_color':
		'var(--message__form__element--hover_color)',
	'--message__form__textarea_color': 'var(--message__form__element_color)',
	'--message__form__textarea_background':
		'var(--message__form__element_background)',
	'--message__form__textarea__placeholder_color':
		'var(--message__form__element__placeholder_color)',

	'--message__form__checkbox__icon_border':
		'var(--message__form__element_color)',
	'--message__form__checkbox__icon_background':
		'var(--message__balloon_background)',
	'--message__form__checkbox--checked__icon_border':
		'var(--message__form__checkbox__icon_border)',
	'--message__form__checkbox--checked__icon_background':
		'var(--message__form__element--active_background)',

	// SUMMARY
	'--message__summary__item_color': 'var(--message__balloon_color)',
	'--message__summary__item__label_color':
		'var(--message__summary__item_color)',
	'--message__summary__item__value_color':
		'var(--message__summary__item_color)',

	// COLLAPSIBLE
	'--message__collapsible__header_color': 'var(--message__balloon_color)',
	'--message__collapsible__header_background':
		'var(--message__balloon_background)',
	'--message__collapsible__header--hover_background':
		'var(--theme--background-hover)',
	'--message__collapsible__divider_background':
		'var(--message__collapsible__header--hover_background)',
	'--message__collapsible__toggle_color':
		'var(--message__collapsible__header_color)',
	// ACCORDION
	'--message__accordion_color': 'var(--message__balloon_color)',
	'--message__accordion_background': 'var(--message__balloon_background)',
	'--message__accordion__header_color': 'var(--message__accordion_color)',
	'--message__accordion__header_background':
		'var(--message__accordion_background)',
	'--message__accordion__title_color': 'var(--message__accordion_color)',
	'--message__accordion__description_color':
		'var(--message__accordion__title_color)',
	'--message__accordion__content_background':
		'var(--message__accordion_background)',
	'--message__accordion__text_color': 'var(--message__accordion_color)',
	'--message__accordion__form__submit_color': 'var(--theme--secondary)',
	'--message__accordion__text__link_color':
		'var(--message__accordion__title_color)',
	'--message__accordion__form__input_border': 'var(--theme--background-hover)',

	'--message__image__details_background': 'var(--message__balloon_background)',
	'--message__image_background': 'var(--theme--light-primary)',

	'--message--right__image__details_background':
		'var(--message--right__balloon_background)',
	'--message--right__image_background': 'var(--message__balloon_background)',

	// CAROUSEL
	'--carousel_color': 'var(--theme--text)',
	'--carousel_background': 'var(--message__balloon_background)',
	'--carousel__title': 'var(--carousel_color)',
	'--carousel__description': 'var(--carousel_color)',
	'--carousel__description__link_color': 'var(--carousel_color)',
	'--carousel__action_color': 'var(--button_color)',
	'--carousel__action_background': 'var(--button_background)',
	'--carousel__action--hover_background': 'var(--button--hover_background)',
	'--carousel__action--hover_color': 'var(--button--hover_color)',
	'--carousel__action__separator_color': 'var(--carousel_background)',
	'--carousel--simple__action_border': 'var(--carousel_color)',
	'--carousel--simple__action_color': 'var(--carousel_color)',
	'--carousel--simple__action_background': theme.mixins.hexWithOpacity(
		theme.colors.white,
		0.85,
	),
	'--carousel--simple__overlay_background': 'var(--theme--light-primary)',
	'--carousel__arrow_color': 'var(--carousel_color)',

	'--message__dots': 'var(--message_color)',

	'--form__element__placeholder_color--opacity': 0.4,

	'--pattern_color': 'var(--color--white)',
	'--pattern_opacity': 0.1,

	'&.theme--blue-mustard': {
		'--theme--primary': 'var(--color--dark-blue)',
		'--theme--secondary': 'var(--color--mustard)',
		'--theme--danger': 'var(--color--bordeaux)',
		'--theme--light-primary': 'var(--color--light-blue)',
		'--theme--light-secondary': 'var(--color--light)',
		'--theme--background': 'var(--color--lightest-blue)',
		'--theme--light-text': theme.mixins.mixColors(
			theme.colors.darkBlue,
			theme.colors.white,
			60,
		),
		'--theme--text': 'var(--theme--primary)',
		'--form__element__placeholder_color': 'var(--theme--light-primary)',
		'--theme--background-hover': 'var(--theme--light-secondary)',
		'--carousel--simple__overlay_background': 'var(--theme--secondary)',
		'--toggler_color': 'var(--theme--light-secondary)',
		'--message__accordion__form__input_border': 'var(--color--light-blue)',
		'--chat__frame_border': 'var(--color--dark-blue)',
		'--message__form__rating__item_color': theme.mixins.adjustLightness(
			theme.colors.light,
			-8,
		),
		'--chat__modal__list__icon_color': 'var(--color--dark-blue)',
		'--message__avatar__icon_color': 'var(--color--dark-blue)',
		'--message__avatar__shape_color': 'var(--color--mustard)',
		'--message__avatar--agent__shape_color': 'var(--theme--light-primary)',
		'--message__avatar--user__shape_color': 'var(--theme--light-secondary)',
		'--pattern_color': 'var(--theme--light-primary)',
	},

	'&.theme--green': {
		'--theme--primary': '#009688',
		'--theme--secondary': '#8bc34a',
		'--theme--light-primary': '#a6ded9',
		'--theme--light-secondary': '#c8dbb2',
		'--message__avatar--agent__icon_color': 'var(--theme--primary)',
		'--pattern_color': 'var(--theme--secondary)',
	},

	'&.theme--sand': {
		'--theme--primary': '#aa8439',
		'--theme--secondary': '#402e32',
		'--theme--light-primary': '#a68d65',
		'--theme--light-secondary': '#fff8ec',
		'--pattern_color': 'var(--theme--primary)',
	},

	'&.theme--purple': {
		'--theme--primary': '#e800ff',
		'--theme--secondary': '#402841',
		'--theme--light-primary': '#a985aa',
		'--theme--light-secondary': '#fff5ff',
		'--pattern_color': 'var(--theme--light-primary)',
	},

	'&.theme--rouge': {
		'--theme--primary': '#e32e2e',
		'--theme--secondary': '#c24f79',
		'--theme--light-primary': '#ffbde4',
		'--theme--light-secondary': '#fe85ad',
		'--pattern_color': 'var(--theme--light-secondary)',
	},

	'&.theme--snow': {
		'--theme--light-primary': 'var(--color--darker-gray)',
		'--theme--light-secondary': 'var(--color--mustard)',
		'--theme--primary': 'var(--color--white)',
		'--theme--secondary': 'var(--color--dark)',
		'--theme--text': 'var(--color--black)',
		'--message__balloon_background': '#f7f7f7',
		'--chat__header_color': 'var(--theme--text)',
		'--chat__frame_border': 'var(--theme--light-primary)',
		'--chat__footer__form__action_color': 'var(--theme--secondary)',
		'--button_background': 'var(--theme--secondary)',
		'--button_color': 'var(--theme--primary)',
		'--button--hover_background': 'var(--theme--light-primary)',
		'--message__avatar__shape_color': 'var(--theme--background)',
		'--toggler_border': 'var(--toggler--secondary)',
		'--button--secondary_background': 'var(--color--lighter-gray)',
		'--button--secondary_color': 'var(--theme--secondary)',
		'--button--secondary--hover_background': 'var(--color--light-gray)',
		'--pattern_color': 'var(--theme--light-secondary)',
		'& .wave .stroke': {
			strokeWidth: '1px',
		},
	},

	'&.theme--dark-blue': {
		'--theme--primary': 'var(--color--dark-blue)',
		'--theme--secondary': 'var(--color--mustard)',
		'--theme--danger': '#e24949',
		'--theme--light-primary': 'var(--color--light-blue)',
		'--theme--light-secondary': 'var(--color--light)',
		'--theme--text': 'var(--color--white)',
		'--theme--light-text': 'var(--theme--light-primary)',
		'--theme--background': theme.mixins.mixColors(
			theme.colors.darkBlue,
			theme.colors.white,
			30,
		),
		'--theme--background-hover': theme.mixins.adjustLightness(
			theme.mixins.mixColors(theme.colors.darkBlue, theme.colors.white, 30),
			10,
		),
		'--form__element__placeholder_color': 'var(--theme--light-text)',
		'--form__help_color': theme.mixins.adjustLightness(
			theme.colors.darkBlue,
			25,
		),
		'--form__element_background': theme.mixins.mixColors(
			theme.colors.darkBlue,
			theme.colors.white,
			90,
		),
		'--toggler_color': 'var(--theme--light-secondary)',
		'--chat__frame_background': 'var(--theme--primary)',
		'--chat__header_color': 'var(--theme--light-secondary)',
		'--chat__footer_color': 'var(--theme--primary)',
		'--chat__footer_background': 'var(--theme--text)',
		'--chat__footer__form__action_color': 'var(--theme--primary)',
		'--chat__footer__powered-by_color': 'var(--theme--text)',
		'--carousel__action_background': 'var(--theme--light-primary)',
		'--carousel_background': 'var(--color--light-gray)',
		'--carousel_color': 'var(--theme--primary)',
		'--carousel__arrow_color': 'var(--theme--light-text)',
		'--button--disabled_opacity': 0.8,
		'--message__balloon_background': theme.mixins.mixColors(
			theme.colors.lightBlue,
			theme.colors.white,
			10,
		),
		'--message__avatar__icon_color': 'var(--theme--primary)',
		'--message__avatar__shape_color': 'var(--theme--secondary)',
		'--message__form__element__label_color': 'var(--theme--primary)',
		'--message_color': 'var(--theme--primary)',
		'--message__quick-replies__option--hover_color': 'var(--theme--text)',
		'--chat__modal_background': 'var(--theme--text)',
		'--chat__modal_color': 'var(--theme--primary)',
		'--button_background': 'var(--theme--light-primary)',
		'--button_color': 'var(--theme--primary)',
		'--button--hover_background': 'var(--theme--secondary)',
		'--button--hover_color': 'var(--theme--text)',
		'--button--secondary_color': 'var(--theme--primary)',
		'--chat__wave_border': 'var(--theme--text)',
		'--chat__wave_border_width': '1px',
		'--pattern_color': 'var(--theme--light-primary)',
	},

	'&.theme--dark': {
		'--theme--primary': 'var(--color--dark)',
		'--theme--secondary': 'var(--color--mustard)',
		'--theme--danger': '#e24949',
		'--theme--light-primary': 'var(--color--light-blue)',
		'--theme--light-secondary': 'var(--color--light)',
		'--theme--text': 'var(--theme--light-secondary)',
		'--theme--background': 'var(--theme--primary)',
		'--theme--background-hover': theme.mixins.adjustLightness(
			theme.colors.dark,
			10,
		),
		'--theme--light-text': theme.mixins.hexWithOpacity(theme.colors.light, 0.6),
		'--toggler_color': 'var(--theme--light-secondary)',
		'--chat__header_color': 'var(--theme--light-secondary)',
		'--chat__frame_background': 'var(--color--black)',
		'--chat__footer_color': 'var(--color--white)',
		'--chat__footer__form__action--disabled_color': 'var(--theme--light-text)',
		'--chat__footer__form__attach--hover_color': 'var(--theme--light-primary)',
		'--chat__footer__form__action_color': 'var(--theme--light-secondary)',
		'--message__sender_color': 'var(--color--white)',
		'--form__element_background': theme.mixins.adjustLightness(
			theme.colors.dark,
			-10,
		),
		'--message__accordion__form__input_border': theme.mixins.adjustLightness(
			theme.colors.dark,
			-10,
		),
		'--carousel__action_background': 'var(--theme--light-primary)',
		'--carousel--simple__overlay_background': 'var(--theme--secondary)',
		'--carousel__action--hover_background': 'var(--theme--secondary)',
		'--message__avatar__icon_color': 'var(--color--dark-blue)',
		'--message__avatar__shape_color': 'var(--theme--secondary)',
		'--button_background': 'var(--theme--light-secondary)',
		'--button_color': 'var(--theme--primary)',
		'--button--hover_background': 'var(--theme--secondary)',
		'--button--hover_color': 'var(--theme--text)',
		'--button--secondary_color': 'var(--theme--primary)',
		'--chat__modal_background': 'var(--theme--primary)',
		'--chat__modal_color': 'var(--theme--text)',
		'--button--disabled_opacity': 0.8,
		'--chat__wave_border': 'var(--theme--text)',
		'--chat__wave_border_width': '1px',
	},

	'&.theme--wa-light': {
		'--theme--primary': '#00a884',
		'--theme--light-primary': '#d9fdd3',
		'--theme--text': '#111b21',
		'--theme--light-text': 'var(--theme--text)',
		'--theme--background': 'var(--color--white)',
		'--theme--background-hover': '#f5f6f6',
		'--pattern_color': '#f5f2ec',
		'--pattern_opacity': 0.4,
		'--chat__frame_background': theme.mixins.adjustLightness('#efeae2', -4),
		'--chat__footer_background': '#f0f2f5',
		'--chat__footer__powered-by_background': 'var(--color--white)',
		'--chat__footer__powered-by_border':
			'var(--chat__footer__powered-by_background)',
		'--chat__footer__form__action_color': '#54656f',
		'--chat__footer__form__action--disabled_color': theme.mixins.hexWithOpacity(
			'#54656f',
			0.6,
		),
		'--chat__footer__form__action--hover_color':
			'var(--chat__footer__form__action_color)',
		'--chat__footer__form__submit_color':
			'var(--chat__footer__form__action_color)',
		'--button--hover_background': '#008069',
		'--message--right__balloon_background': '#d9fdd3',
		'--message__balloon_background': 'var(--color--white)',
		'--message_color': '#111b21',
		'--chat__header__close_color': '#54656f',
		'--chat__header__close_border': 'var(--chat__header__close_color)',
		'--chat__header__logo_color': '#f0f2f5',
		'--toggler--secondary': 'var(--color--white)',
		'--chat__header_background': 'var(--color--white)',
		'--chat__header_color': 'var(--theme--text)',
		'--chat__wave_border_width': '3px',
		'--message__avatar__icon_color': 'var(--color--white)',
		'--message__avatar__shape_color': '#dfe5e7',
		'--message__avatar--agent__icon_color':
			'var(--message__avatar__icon_color)',
		'--message__avatar--user__icon_color': 'var(--theme--primary)',
		'--message__avatar--system__icon_color':
			'var(--message__avatar__icon_color)',
		'--message__avatar--user__shape_color':
			'var(--message__avatar__shape_color)',
		'--message__avatar--agent__shape_color':
			'var(--message__avatar__shape_color)',
		'--message__avatar--system__shape_color':
			'var(--message__avatar__shape_color)',
		'--message__image_background': '#f0f2f5',
		'--form__element_background': '#f5f6f6',
		'--form__element__placeholder_color': '#667781',
		'--message__form__element__placeholder_color':
			'var(--form__element__placeholder_color)',
		'--message__form__element_background': 'var(--form__element_background)',
		'--chat__scrollbar_color': theme.mixins.hexWithOpacity('#0b141a', 0.2),
		'& .toggler-button .floater__icon .dot.secondary': {
			fill: 'var(--theme--primary)',
		},
		'& .header__body': {
			marginBottom: '1px',
		},
		'& .header__title': {
			fontWeight: 400,
		},
		'& .message__text': {
			fontWeight: 400,
			fontFamily:
				"'Segoe UI', 'Helvetica Neue', Helvetica, 'Lucida Grande', Arial, Ubuntu, Cantarell, 'Fira Sans', sans-serif",
		},
		'& .message__text .message__time': {
			color: '#667781',
		},
		'& .message__avatar .icon': {
			stroke: 'none',
		},
		'& .chat__footer .form__content': {
			marginRight: 0,
		},
		'& .chat__footer .form__actions': {
			alignItems: 'center',
		},
		'& .chat__footer .form__attach': {
			display: 'flex',
			alignItems: 'center',
		},
		'& .chat__main::-webkit-scrollbar': {
			height: '0.2em',
			width: '0.2em',
		},
		'& .toggler-button--unread': {
			'--toggler--primary': 'var(--theme--primary)',
			'--toggler--secondary': 'var(--color--white)',
			'& .floater__icon .dot.secondary': {
				fill: 'var(--theme--primary)',
			},
		},
	},
}));
